import "./help.css"

import { Capacitor } from '@capacitor/core'
import { FaCalendarTimes, FaStreetView } from 'react-icons/fa'
import { HiDotsHorizontal } from 'react-icons/hi'
import {
	IonAccordion,
	IonAccordionGroup,
	IonButton,
	IonIcon,
	IonItem,
	IonLabel,
} from '@ionic/react'
import { TiThSmallOutline, TiTimes } from 'react-icons/ti'
import { analyticsOutline, ellipse, mail, shareOutline, checkmarkSharp, optionsOutline, recordingOutline, settingsSharp, bulbOutline, people, cloudDoneOutline, add, settingsOutline } from 'ionicons/icons'
import React, { useEffect, useState } from 'react'

import { DisplayUpgradeOffer, PlanName } from './Plan'
import { newestNewsItemDate } from './newsItems'
import { saveToLocalStorage } from './util'
import { APP_STORE_URL, GOOGLE_PLAY_STORE_URL, tabButtons } from './definitions'
import {
  useAmActiveTab,
  useClub,
  useHelpTabState,
  useNewsNotificationState,
  useSection,
  useUser,
} from './data'
import Icon from './Icon'
import News from './News'
import { MoreInfoLink } from './MoreInfoLink'
import comboImg from './media/combos.png'
import posPrefsImg from './media/pos-prefs.png'
import introRosterMp4 from './media/intro-roster.mp4'
import OrangeIcon from './OrangeIcon'
import flowEvents from './media/flow-events.png'
import flowEvents2 from './media/flow-events-2.png'
import flowSequence from './media/flow-sequence.png'
import flowDominance from './media/flow-dominance.png'
import flowMap from './media/flow-map.png'
import flowConversion from './media/flow-conversion.png'
import flowStats from './media/flow-stats.png'
import flowTidy from './media/flow-tidy.png'

import tutorialRecordTurnovers from './media/tutorial-record-turnovers.mp4'

import packageInfo from '../package.json'

import DownloadOnTheAppStoreImage from './media/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import GetItOnGooglePlayImage from './media/GetItOnGooglePlay_Badge_Web_color_English.svg'

import subImg from './media/sub.png'
import raincloudImg from './media/raincloud.png'
import pinImg from './media/pin.png'
import subDemoClip from './media/sub-demo.mp4'
import subDemoClip2 from './media/sub-demo-2.mp4'
import fivePlayersImg from './media/five-players.png'
import FillinIcon from "./FillinIcon"
import {training} from "./media/trainingIcon"
import netballConnectImage from './media/netball-connect.png'
import playHqImage from './media/playhq.png'
import MaleIcon from "./MaleIcon"
import SampleGoButton from "./SampleGoButton"
import {FlowInviteGallery} from "./FlowInviteUpsell"
import GameInsightIcon from "./GameInsightIcon"
import TutorialRecordFlowsModal from "./TutorialRecordFlows"
import QuickStartLink from './QuickStartLink'

const Help = () => {

	const amActiveTab = useAmActiveTab('help')

	const [ newsNotification, setNewsNotification ] = useNewsNotificationState()

	const [ helpTab, setHelpTab ] = useHelpTabState()
	const [ subTab, setSubTab ] = useState<string | null>(null)

	const hash = window.location.hash

	//
	// Open / scroll correct value
	//
	// If we're sent a hash like '/help#stats-faq' then we have to open the Stats
	// Accordion AND the Stats -> FAQ Accordion, wait a sec, then scroll to it.
	//
	// Note this can overlap a bit with MoreInfoLink, but that's okay. We need
	// to handle both cases depending on whether the user is already on the Help
	// page or not.
	//
	useEffect(() => {
		// console.log('triggered', hash, amActiveTab)
		if (amActiveTab) {
			if (hash) {
				setTimeout(() => {
					const str = hash.substring(1)
					const [ newValue, newSubValue ] = str.split('-')
					setHelpTab(newValue)
					if (newSubValue) {
						setSubTab(str)
						setTimeout(() => {
							const [ el ] = document.getElementsByClassName(`help-${str}`)
							el?.scrollIntoView({ behavior: 'smooth' })
						}, 750)
					} else {
						setSubTab(null)
					}
				}, 250)
			} else {
				saveToLocalStorage('viewedNewsPage', newestNewsItemDate)
				if (newsNotification) {
					setHelpTab('news')
					setNewsNotification(false)
				}
			}
		}
	}, [ amActiveTab, newsNotification, setNewsNotification, setHelpTab, hash ])

	const platform = Capacitor.getPlatform()
	const versionInfo = `${packageInfo?.version}-${platform}`

	return (

		<section className="help">

			<div className="help-title">
				<Icon />
				<h1>
					AutoRoster
					<span className="app-info">
						{versionInfo}
					</span>
				</h1>

			</div>

			<IonAccordionGroup
				value={helpTab}
				className="master-help"
				onIonChange={(e: CustomEvent) => {
					const newValue = e.detail.value
					setHelpTab(newValue)
				}}
			>

				<IonAccordion value="news">
					<IonItem slot="header" color="light">
						<IonLabel>
							News
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<News
							setHelpTab={setHelpTab}
						/>
					</div>
				</IonAccordion>

				<IonAccordion value="createTeam">
					<IonItem slot="header" color="light">
						<IonLabel>
							Quick Start
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpCreateTeam />
					</div>
				</IonAccordion>

				<IonAccordion value="roster">
					<IonItem slot="header" color="light">
						<IonLabel>
							Generate a roster
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpRoster />
					</div>
				</IonAccordion>

				<IonAccordion value="stats">
					<IonItem slot="header" color="light">
						<IonLabel>
                            <IonIcon icon={analyticsOutline} />
                            <span>Stats</span>
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpStats subTab={subTab} setSubTab={setSubTab} />
					</div>
				</IonAccordion>

                <IonAccordion value="insight">
					<IonItem slot="header" color="light">
						<IonLabel>
                            <GameInsightIcon />
                            <span>Flow Maps</span>
                        </IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpInsight />
					</div>
				</IonAccordion>


				<IonAccordion value="autorosterpro">
					<IonItem slot="header" color="light">
						<IonLabel>
							<PlanName />
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpAutoRosterPro />
					</div>
				</IonAccordion>

				<IonAccordion value="tips">
					<IonItem slot="header" color="light">
						<IonLabel>
							Tips & Tricks
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpTips subTab={subTab} setSubTab={setSubTab} />
					</div>
				</IonAccordion>

				<IonAccordion value="sharing">
					<IonItem slot="header" color="light">
						<IonLabel>
							Sharing
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpSharing />
					</div>
				</IonAccordion>

				<IonAccordion value="troubleshooting">
					<IonItem slot="header" color="light">
						<IonLabel>
							Troubleshooting
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpTroubleshooting />
					</div>
				</IonAccordion>

				<IonAccordion value="clubs">
					<IonItem slot="header" color="light">
						<IonLabel>
							Clubs
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpClubs />
					</div>
				</IonAccordion>

				<IonAccordion value="privacy">
					<IonItem slot="header" color="light">
						<IonLabel>
							Privacy policy
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpPrivacy />
					</div>
				</IonAccordion>

				<IonAccordion value="terms">
					<IonItem slot="header" color="light">
						<IonLabel>
							Terms of Service
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpTerms />
					</div>
				</IonAccordion>

				<IonAccordion value="about">
					<IonItem slot="header" color="light">
						<IonLabel>
							About
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<About />
					</div>
				</IonAccordion>

			</IonAccordionGroup>

			<p className="help-bottom-buttons">
				<EmailMax />
				<span>
					max_barry@maxbarry.com
				</span>
			</p>

		</section>
	)
}

type NavItemProps = {
	tab?: string
	label?: string
	icon?: string | React.ElementType
	img?: string
	IconElement?: React.ElementType
	iconClassName?: string
}

export const NavItem = (props: NavItemProps) => {

	const { tab, img, IconElement, iconClassName } = props

	if (!tab && !props.label && !img && !IconElement && !props.icon)
		return null

	const info = (tab && tabButtons.find(obj => obj.id === tab)) || undefined

	const myIcon = props.icon || info?.icon
	const myLabel = props.label || info?.label
	const iconElement = myIcon
		? (typeof myIcon === 'function'
			? React.createElement(myIcon, { className: iconClassName })
			: <IonIcon icon={myIcon} className={iconClassName} />)
		: null

	return (
		<span className="help-navitem">
			{
				img ? (
					<img src={img} alt={myLabel} className={iconClassName} />
				) : IconElement ? (
					<IconElement className={iconClassName} />
				) : iconElement
			}
			{myLabel}
		</span>
	)
}

const NavItemToggleView = () => (
	<NavItem IconElement={TiThSmallOutline} label="View" />
)

const HelpAutoRosterPro = () => (
	<div>
		<h3>
			<PlanName />
		</h3>
		<p>
			AutoRoster is free (and ad-free!) for managing one team and one season.
		</p>
		<p>
			If you need another team or season, you can delete
			your current one, or subscribe to <PlanName />.
		</p>
        <DisplayUpgradeOffer noHelpLink={true} />
        <p>
            Benefits of <PlanName />:
        </p>
        <ul>
            <li>
                <p>
                    Keep all your data &mdash; as many teams and seasons as you like,
                    for as long as you like, so you can review past performance.
                </p>
            </li>
            <li>
                <p>
                    Access to <MoreInfoLink text="Combinations" helpSection="stats" helpSubSection="pairings" />,
                    which provides stats on the performance of all Pairs and Trios.
                </p>
			</li>
            <li>
                <p>
                    Access to <MoreInfoLink text="Flow Maps" helpSection="insight" />,
                    which lets you analyse team ball movement.
                </p>
            </li>
		</ul>

        <FlowInviteGallery />

		<p>
			Clubs can subscribe to "<PlanName isClub={true} />" to offer these benefits
            to all their coaches.
            <MoreInfoLink helpSection={"clubs"} text="More about Clubs..." />
		</p>
        <p>
            A free 30-day trial is usually available to personal users (not clubs)
            signing up for the first time.
        </p>
	</div>
)

// Also used in Club.js
export const HelpClubsText = () => (
	<div>
		<h3>
			<span className="offer-appname">
				AutoRoster
			</span>
			{' '}
			<span className="offer-planname">
				for Netball Clubs
			</span>
		</h3>
		<table className="club-feature-table">
			<thead>
				<tr>
					<th></th>
					<th>Free</th>
					<th><PlanName isClub={true} /></th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Teams can have multiple coaches</td>
					<td><IonIcon icon={checkmarkSharp} /></td>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
				<tr>
					<td>Club admin can reassign teams from one coach to another</td>
					<td><IonIcon icon={checkmarkSharp} /></td>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
				<tr>
					<td>Club admin can see team rosters and stats</td>
					<td><IonIcon icon={checkmarkSharp} /></td>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
				<tr>
					<td>Coaches can have multiple teams</td>
					<td/>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
				<tr>
					<td>Coaches can have multiple seasons per team</td>
					<td/>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
				<tr>
					<td>
                        Full player combinations stats
                        <MoreInfoLink text="" helpSection="stats" helpSubSection="pairings" />

                    </td>
					<td/>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
                <tr>
                    <td>
                        Flow Maps
                        <MoreInfoLink text="" helpSection="insight" />
                    </td>
					<td/>
					<td><IonIcon icon={checkmarkSharp} /></td>
				</tr>
			</tbody>
		</table>
		<p>
			Instead of coaches individually buying <PlanName /> for extra data storage
			and stats, you can grant it to all of them via a single <b><PlanName isClub={true} /></b> subscription.
		</p>

        <p className="help-special-text club-promotional-special-text">
			🌟 New in 2026! <b><PlanName isClub={true} /></b> is currently half-price
			to support community netball clubs. A$299 per year covers up to 40 coaches.
		</p>

        <QuickStartLink />
	</div>
)

export const OpenClubLink = () => {

	const user = useUser()

	if (!user)
		return null

	const onViewClub = async () => {
		document.getElementById('trigger-club')?.click()
	}

	return (
		<div className="open-profile-button-container">
			<IonButton
				onClick={onViewClub}
				className="open-profile-button"
				fill="outline"
			>
				View Club
			</IonButton>
		</div>
	)
}

const HelpClubs = () => (
	<div>
		<HelpClubsText />
		<OpenClubLink />
	</div>
)

const HelpCreateTeam = () => {

    const platforms: AppStorePlatform[] = [ ]
    if (!Capacitor.isNativePlatform()) {
        const ua = navigator.userAgent
        const isAndroid = /android/i.test(ua)
        const isIOS = /iPad|iPhone|iPod/.test(ua)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        if (isAndroid) {
            platforms.push('android')
        } else if (isIOS) {
            platforms.push('ios')
        } else {
            platforms.push('ios', 'android')
        }
    }

    return (
        <>
            <QuickStartLink />
            <AppStoreButtons platforms={platforms} />
            <ol className="help-quick-start">
                <li>
                    <h3>
                        Sign In
                    </h3>
                    <p>
                        Choose <span className="help-fake-button">Create account</span> or <span className="help-fake-button-outline">Sign in</span>.
                    </p>
                    <p className="help-special-text">
                        <IonIcon icon={people} /> Joining a club? If you clicked an invitation link to join a club on
                        AutoRoster, after you're signed in you'll see <b>Join (club name)</b>. Tap that,
                        then <b>Continue</b>
                    </p>
                </li>
                <li>
                    <h3>
                        Add a Team
                    </h3>
                    <p>
                        Tap <span className="help-fake-button"><IonIcon icon={add} /> Add Team</span>, enter a name, and
                        tap <span className="help-fake-button"><IonIcon icon={checkmarkSharp} /> Submit</span>
                    </p>
                    <p className="help-special-text">
                        <IonIcon icon={people} /> Club admin can assign coaches to any existing AutoRoster teams they have,
                        preserving their history. If you're supposed to take over one of your club's existing
                        teams on AutoRoster, rather than create a new one, tell your club admin you're ready to be added as the team's coach.
                        Any teams to which they add you as coach can be found in your Team Selector via the top-right Home button.
                    </p>
                </li>
                <li>
                    <h3>
                        Add a Season
                    </h3>
                    <p>
                        Now you have a team!
                        Tap <span className="help-fake-button"><IonIcon icon={add} /> Add Season</span>
                    </p>
                    <p>
                        <em>
                            Teams can have multiple seasons (e.g. "Spring 2026", "Parkville Tournament"). However, the
                            free version of AutoRoster only supports one season at a time.
                        </em>
                    </p>
                    <p>
                        If your team already has a season, scroll
                        to the bottom of your <NavItem tab="games" /> tab, then
                        tap <span className="help-fake-button-outline"><IonIcon icon={settingsOutline} /> Season settings</span>
                        {' '}
                        then <span className="help-fake-button-outline">+ Add New Season for (Your Team)</span>
                    </p>
                    <p>
                        Enter a name for your season, choose your settings, then
                        tap <span className="help-fake-button"><IonIcon icon={checkmarkSharp} /> Create Season</span>
                    </p>
                    <p className="help-special-text">
                        <IonIcon icon={cloudDoneOutline} /> <b>Fixture Sync</b> is highly recommended for leagues
                        on NetballConnect or PlayHQ/NetballHQ, as it usually saves you from having to manually input games
                        and scores. When creating a season, tap <b>Fixture Sync:</b> <span className="help-fake-button-outline">Connect...</span> and
                        follow the instructions. You can also set it up later.
                    </p>
                </li>
                <li>
                    <h3>
                        Add Players
                    </h3>
                    <p>
                        Tap the <NavItem tab="players" /> tab. Then:
                    </p>
                    <ol>
                        <li>
                            <p>
                                Tap <span className="help-fake-button"><IonIcon icon={add} /> Add Player</span>
                            </p>
                        </li>
                        <li>
                            <p>
                                Enter a player's name. You can change this later.
                            </p>
                        </li>
                        <li>
                            <p>
                                Tap <span className="help-fake-button"><IonIcon icon={checkmarkSharp} /> Submit</span>
                            </p>
                        </li>
                        <li>
                            <p>
                                Continue adding players as needed.
                            </p>
                        </li>
                    </ol>
                </li>
                <li>
                    <h3>
                        Add Games
                    </h3>
                    <p>
                        If you set up Fixture Sync, AutoRoster will offer to automatically import your games. But
                        to add games manually:
                    </p>
                    <ol>
                        <li>
                            <p>
                                From the <NavItem tab="games" /> tab, tap <span className="help-fake-button"><IonIcon icon={add} /> Add Game</span>
                            </p>
                        </li>
                        <li>
                            <p>
                                Enter game details. You can change these later.
                            </p>
                        </li>
                        <li>
                            <p>
                                Tap <span className="help-fake-button"><IonIcon icon={checkmarkSharp} /> Submit</span>
                            </p>
                        </li>
                        <li>
                            <p>
                                Continue adding games as needed.
                            </p>
                        </li>
                    </ol>
                </li>
            </ol>
        </>
    )
}

type AppStorePlatform = 'android' | 'ios'

const AppStoreButtons = (props: { platforms: AppStorePlatform[] }) => {

    const { platforms } = props

    if (!platforms || !platforms.length)
        return null

    const stores = {
        ios: { name: 'iOS', url: APP_STORE_URL, img: DownloadOnTheAppStoreImage },
        android: { name: 'Android', url: GOOGLE_PLAY_STORE_URL, img: GetItOnGooglePlayImage },
    } as const

    const content = platforms.map(platform => {
        const store = stores[platform]
        return (
            <a key={platform} className="app-store-download" href={store.url}>
                <img src={store.img} alt={`Download AutoRoster for ${store.name}`} />
            </a>
        )
    })

    return (
        <p className="help-special-text">
            <IonIcon icon={bulbOutline} />
            {' '}
            AutoRoster is available as an app! Install now for easier rostering.
            <span className="app-store-download-buttons">
                {content}
            </span>
        </p>
    )
}

const HelpRoster = () => (
	<>
		<p>
			From your <NavItem tab="games" /> tab, tap any individual game to view its roster.
		</p>
		<p>
			Tap <NavItem label="Edit" /> to enter Edit mode.
		</p>

	        <p className="help-special-text">
	            <b>No Edit button?</b> You may not be logged in as a coach of this team.
	            {' '}
	            <MoreInfoLink text="Troubleshooting..." helpSection="troubleshooting" />
	        </p>

		<p>
			Once in Edit Mode, you can:
		</p>
		<ul>
			<li>
				<p>
					Toggle players' availability by tapping the <NavItem icon={ellipse} iconClassName="tick" label="green dot" /> beside their names.
				</p>
			</li>
			<li>
				<p>
					Tap <SampleGoButton /> to automatically generate a roster.
				</p>
			</li>
			<li>
				<p>
					Tap <NavItemToggleView /> to toggle between player / position views.
				</p>
			</li>
            <li>
                <p>
                    Tap <b><NavItem icon={optionsOutline} label="" /> Dev / Junior / Senior / Custom</b> to
                    set roster preferences.
                </p>
            </li>
			<li>
				<p>
					Tap <NavItem IconElement={FaStreetView} label="Positions" /> to
					enter preferences for particular players, if necessary. (Don't
					overdo it — the more you set here, the less freedom AutoRoster has to move things around.
                    <MoreInfoLink text="More about position preferences..." helpSection="tips" helpSubSection="prefs" />
                    )
				</p>
			</li>
            <li>
				<p>
					Nominate a captain / orange provider by
					tapping the <NavItem IconElement={OrangeIcon} iconClassName="orange-image" label="Oranges" /> box.
				</p>
			</li>
		</ul>

		<p>
			<em>
				Example: Setting a few positional preferences, auto-generating a roster, and
				tweaking it.
			</em>
		</p>

		<div className="video-container">
			<video playsInline autoPlay muted controls>
				<source src={introRosterMp4} type="video/mp4" />
			</video>
		</div>

		<p>
			Rosters can be modified in many ways:
		</p>
		<ul>
			<li>
				<p>
					Drag positions around.
				</p>
			</li>

			<li>
				<p>
					Drag one player's name onto another to swap all their
					positions with each other.
				</p>
			</li>

			<li>
				<p>
                    Swap entire periods by dragging one header cell onto another.
                    For example, drag <b>Q1</b> onto <b>Q2</b> to swap the first two quarters.
				</p>
			</li>

			<li>
				<p>
					Tap <SampleGoButton /> again
					to try to algorithmically improve the current roster.
				</p>
			</li>
		</ul>
		<p>
			AutoRoster tries to find the best solution for the preferences you give it!
			Choose an appropriate preset or tweak preferences by
            tapping <NavItem icon={optionsOutline} label="" />Dev / Junior / Senior / Custom in
		    the top right of a roster page (in Edit Mode),
			then come back and re-run <SampleGoButton />.
		</p>
		<p>
			<b>Tip:</b> Tap any player/position to lock it in place, so it won't
			change when you run <SampleGoButton />.
		</p>
	</>
)

export type HelpInsightSection =  'map' | 'scoring' | 'dominance' | 'chains' | 'conversion-efficiency'

export const HelpInsightScoring = () => (
	<>
		<h3>
			Scoring
		</h3>
		<p>
			A "score worm" showing goals scored by each team. Below, the blue team trailed for most
			of the game before hitting the lead late in the third quarter.
		</p>
		<img src={flowSequence} alt="" className="help-img" />
	</>
)

export const HelpInsightDominance = () => (
	<>
		<h3>
			Dominance
		</h3>
		<p>
			Shows which team was dominating each court third throughout the match. This can be directly
            compared to the Scoring worm, to reveal whether scoring runs were connected to dominance
            in Attack (A), Midcourt (M), Defence (D), or some combination of the above.
		</p>
        <p>
            Colours indicate which side was dominant (if any) in a particular court third at that point in the
            match. This is from your team's perspective; i.e. "A" means your Attack.
        </p>
		<p>
			Below, the user's team (blue) was badly outclassed in the midcourt for most of Q1, but
			their defence was able to stem the bleeding. Q2 was generally mixed, while in Q3 the
			blue defence was dominant again. The blue attack also started to get on top late in Q3. In Q4,
			blue was dominant across the court before a late fade.
		</p>
		<img src={flowDominance} alt="" className="help-img" />
		<p>
			<em>
				Some technical detail: Midcourt dominance is determined by which team is more successfully moving
                the ball through the midcourt. That is, they're retaining possession themselves while forcing the
				opposition into turnovers.
            </em>
        </p>
        <p>
            <em>
                Attack and Defence dominance is a little more complicated. It's determined by whether a team
                is converting attack entries into scores at significantly different rate than the match average.
				For example, if the match average across both teams is 60% of attack entries being converted to scores,
				then periods of play where one team converts at a higher rate will be shown as dominated by
				the attacking team, while periods below 60% will be shown as dominated by the defending team.
			</em>
		</p>
		<p>
			<em>
				Limitation: AutoRoster can't tell the difference between a strong attack and a weak defence. (Or
				a strong defence and a weak attack.) For example, at one end of the court, a team might be
				struggling to score because of outstanding defenders, while at the other end, goalers are
				missing a lot of easy shots and getting no rebounds. A true reading of this play would be that defenders
				are dominating at one end while neither team is dominating at the other, but this isn't something
				AutoRoster is capable of reporting &mdash; it will only see that both teams
				have similar attack-to-score efficiency, and report that neither team is dominating in attack or defence.
			</em>
		</p>
	</>
)

export const HelpInsightChains = () => (
	<>
		<h3>
			Chains
		</h3>
		<p>
			Shows ball movement leading up to each goal. It displays the <b>score</b> when the
			Centre Pass was taken and the court areas the ball moved through (<b>M</b>idcourt, <b>A</b>ttack, <b>D</b>efence).
			Newer events scroll in at the top.
		</p>
		<img src={flowEvents} alt="" className="help-img" />
		<p>
			Above, a game begins with each side scoring directly from a centre pass.
		</p>
		<p>
			Later, with the scores 18 to 24, the red team takes a centre pass but loses the ball in attack. The blue team
			moves the ball down the other end of the court, and scores. (The dashed outline signals
			a goal against the other team's centre pass.)
		</p>
		<img src={flowEvents2} alt="" className="help-img" />
	</>
)

const HelpInsightFlowMap = () => (
	<>
		<h3>
			Flow Map
		</h3>
		<p>
			Visualizes ball movement, showing how often the ball passed through each court area for each team.
		</p>
        <p>
            Although one team's ball movement is shown along the top of the court, and the other team's along
            the bottom, this is purely stylistic. What we're tracking is left and right movement: the ball entering
            and leaving different court thirds.
        </p>
        <div className="help-big-image">
            <img src={flowMap} alt="" className="help-img" />
        </div>
		<p>
			Above, we see the blue team scored <b>26 Goals</b> from <b>44 Attack Entries</b> (times the ball entered the
            goal third). Their other 18 Attack Entries were repelled by the red team.
		</p>
        <p>
            By contrast, the red team scored <b>23 Goals</b> from <b>53 Attack Entries</b> &mdash; more attacks but fewer scores.
            Their remaining 30 Attack Entries were repelled.
        </p>
		<p>
			The blue team had <b>2 Attack Gains</b> &mdash; times they lost possession in attack but regained it
            before the opposition could clear. Red had <b>7 Attack Gains</b>. This suggests that while
            the blue defence was effective at gaining the ball, they often had trouble clearing.
		</p>
		<p>
			Blue has a total of <b>60 Midcourt Entries</b>, which is the sum total of <b>26 Centre Passes</b> plus <b>5 Midcourt Gains</b> plus <b>30 Defence Clearances</b>, with <b>+1</b> ball still in possession at the end of a quarter.
		</p>
        <p>
            Only 44 of Blue's 60 Midcourt balls became Attack Entries because the other 16 were turned over in the midcourt.
        </p>
		<p>
			The light colour shading at court bottom signals that the red team generally had dominance in the midcourt, while the
			blue team performed better at either end of the court.
		</p>
        <p>
            The thickness of the various arrows hints at a fairly strong cycle of ball coming out of blue's defence
            but then being lost to red in the midcourt.
        </p>
        <img src={flowStats} alt="" className="help-img" />
        <p>
			If we were analysing this game, we would note that the blue team turned over the ball 16 times to 5 in the midcourt,
			which led to far fewer attack entries (44 to 53). Ordinarily, this might have cost them the match, but the blue defence
			repelled attacks far more often &mdash; 37 Defence Gains to 20. (Defence Gains include both the times the ball was successfully
            cleared and times when a gain was lost again without clearing the defending third.) Altogether, this was an overwhelming
			defensive effort by Blue almost ruined by an inability to hold onto the ball.
		</p>
        <p>
            All these numbers are relative, so they can't tell us whether Blue has an objectively strong defence or Red has a
            weak attack &mdash; or both! However, it would be suggestive if patterns emerged after
            games against different opponents.
        </p>
	</>
)

export const HelpInsightConversionEfficiency = () => (
	<>
		<h3>
			Conversion Efficiency
		</h3>
		<p>
			The Midcourt panel tracks the conversion of midcourt ball into attack entries &mdash; that is,
            how often each team is able to move the ball from the midcourt into attack without losing possession.
            The Attack panel tracks conversion of attack entries into scores &mdash; what percentage of attacks
            result in a score.
		</p>
		<img src={flowConversion} alt="" className="help-img" />
		<p>
			Here we see the blue team began with a very poor midcourt Q1, where 15 midcourt
			balls resulted in only 7 attack entries, with the other 8 balls turned over (efficiency of 47%).
			By contrast, the red team was strong through the midcourt all game, with an efficiency of 91% (58 midcourt balls for 53 attack entries).
			Elsewhere the story was reversed: Blue consistently converted
			around 60% of attack entries into scores, while red struggled.
			Red was particularly hurt by Q3, when 12 attack entries delivered only 4 goals (efficiency of 33%).
            In the end, the blue team's ability to generate more goals from fewer attack entries was enough for
            a 26-23 victory.
		</p>
		<p>
			<em>
				Note: This feature doesn't support tracking goalshooter accuracy. No stat is generated when a goaler misses
				then recovers the rebound &mdash; only when possession is lost to the other team. AutoRoster doesn't
                currently offer recording of individual player stats, but there are several other sports apps that do.
			</em>
		</p>
	</>
)

export const HelpInsightSectionContent = (props: {
	section: HelpInsightSection
}) => {
	if (props.section === 'scoring') {
		return <HelpInsightScoring />
	} else if (props.section === 'dominance') {
		return <HelpInsightDominance />
	} else if (props.section === 'chains') {
		return <HelpInsightChains />
	} else if (props.section === 'map') {
        return <HelpInsightFlowMap />
    } else if (props.section === 'conversion-efficiency') {
        return <HelpInsightConversionEfficiency />
    }
    return <></>
}

const HelpInsight = () => {

    const [ isHelpOpen, setIsHelpOpen ] = useState(false)

    return (
	    <>
	        <p>
                Flow Maps analyse ball movement (<PlanName /> only).
	        </p>
	        <FlowInviteGallery routerLink="/help/game-insight" />
            <p>
                The dashboard updates live, so coaches can see real-time stats, as well as review after the game.
            </p>
            <h3>
                Recording a Game
            </h3>
            <p>
                Anyone can record a game, even if they don't use AutoRoster, or know much about netball.
            </p>
            <ol>
                <li>
                    <p>
                        From your <NavItem tab="games" /> tab, tap the three-dot menu on any game.
                    </p>
                </li>
                <li>
                    <p>
                        Tap <NavItem label="Flow Map" IconElement={GameInsightIcon} /> then <NavItem label="Record" icon={recordingOutline} />
                    </p>
                </li>
            </ol>
            <p>
                From here, either record the game yourself or invite someone else to do it by
                sending them a link.
            </p>
            <div className="video-container shadowed">
                <video playsInline autoPlay muted loop>
                    <source src={tutorialRecordTurnovers} type="video/mp4" />
                </video>
            </div>
            <IonButton fill="outline" onClick={() => setIsHelpOpen(true)}>
                How to Record a Game
            </IonButton>
            <TutorialRecordFlowsModal
                isOpen={isHelpOpen}
                onDismiss={() => setIsHelpOpen(false)}
            />

            <p>
                <em>
                    For correcting errors after a game, see "Tidy Data" below.
                </em>
            </p>

            <HelpInsightScoring />
            <HelpInsightDominance />
            <HelpInsightChains />
            <HelpInsightFlowMap />
            <HelpInsightConversionEfficiency />

            <h3>
                Tidy Data
            </h3>
            <p>
                Sometimes data recorders make mistakes, and don't realize it.
                You can have AutoRoster fix up any obvious mistakes
                in the recorded data.
            </p>
            <p>
                This relies on your game having official period-by-period scores,
                which you can either enter manually (Games tab &gt; ... &gt; Edit &gt; Result)
                or obtain via Fixture Sync.
            </p>
            <p>
                When a game has completed Flow Map data, has official scores, has at least one
                discrepancy between the two, and is coached by you, you get a "Tidy data" link on
                the <NavItem label="Record" icon={recordingOutline} /> &gt; <NavItem label="Setup" icon={settingsSharp} /> screen.

            </p>
            <p>
                <img src={flowTidy} alt="Flow Tidy" className="help-img" />
            </p>
            <p>
                Tap this to review AutoRoster's suggestions for modifying the recorded
                Flow Map data so it matches the official scores.
            </p>

	    </>
	)
}

type HelpSubAccordionProps = {
	subTab: string | null
	setSubTab: (value: string | null) => void
}

const HelpStats = (props: HelpSubAccordionProps) => {

	const { subTab, setSubTab } = props

	return (
		<>

			<p>
				AutoRoster can provide advanced stats to improve your team rosters.
				Most of these rely on period-by-period scores. After each match,
				enter the running score at the end of each period, including the final score.
			</p>
			<p>
				To enter match scores:
			</p>
			<ul>
				<li>
					<p>
						In the <NavItem tab="games" /> tab, past games will invite you to <b>Enter Scores</b>.
					</p>
					<p>
						You can also change scores later in the same place by
						tapping the three dots (<HiDotsHorizontal />) beside
						a game and then <b>Edit Game</b>.
					</p>
				</li>
			</ul>
			<p>
				To view stats directly on rosters (not just in the Stats tab):
			</p>
			<ul>
				<li>
					<p>
						On a roster page in Edit mode, tap <NavItem icon={optionsOutline} label="Settings" /> in the top right,
						then toggle on <b>Show NetStats</b>. If you have <PlanName />, you can
						also toggle on <b>Pair/Trio Stats</b>.
					</p>
				</li>
			</ul>

			<p>
				<img
					className="comboImg"
					src={comboImg}
					alt="Combinations"
				/>
			</p>

			<p>
				<em>
					Example: This is a very good roster, with lots of positive scores
					for <b>NetStats</b> (how the team performs when this
					player is in this position) as well as <b>Pairs</b> and <b>Trios</b> (how
					the team performs when this combination of players have these positions together).
				</em>
			</p>

			<IonAccordionGroup
				value={subTab}
				onIonChange={(e: CustomEvent) => {
					e.stopPropagation()
					const newSubValue = e?.detail?.value
					setSubTab(newSubValue)
				}}
			>

				<IonAccordion value="stats-positions" className="help-stats-positions">
					<IonItem slot="header" color="light">
						<IonLabel>
							Positions Played
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpPositionsPlayed />
					</div>
				</IonAccordion>

				<IonAccordion value="stats-netStats" className="help-stats-netStats">
					<IonItem slot="header" color="light">
						<IonLabel>
							NetStats
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpNetStats />
					</div>
				</IonAccordion>

				<IonAccordion value="stats-pairings" className="help-stats-pairings">
					<IonItem slot="header" color="light">
						<IonLabel>
							Combinations (Pairs/Trios)
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpPairings />
					</div>
				</IonAccordion>

				<IonAccordion value="stats-best" className="help-stats-best">
					<IonItem slot="header" color="light">
						<IonLabel>
							Best Quarters
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpBestQtrs />
					</div>
				</IonAccordion>

				<IonAccordion value="stats-orange" className="help-stats-orange">
					<IonItem slot="header" color="light">
						<IonLabel>
							Attendance, Captains & Fill-Ins
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpAbsentees />
					</div>
				</IonAccordion>

				<IonAccordion value="stats-howitworks" className="help-stats-howitworks">
					<IonItem slot="header" color="light">
						<IonLabel>
							How It Works
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpStatsIntro />
					</div>
				</IonAccordion>


				<IonAccordion value="stats-faq" className="help-stats-faq">
					<IonItem slot="header" color="light">
						<IonLabel>
							Stats FAQ
						</IonLabel>
					</IonItem>
					<div className="ion-padding" slot="content">
						<HelpStatsFAQ />
					</div>
				</IonAccordion>

			</IonAccordionGroup>
		</>
	)
}

const HelpStatsIntro = () => (
	<>

		<p>
			AutoRoster can't watch the game, but it can detect correlations between
			team performance and who's playing where.
		</p>

		<p>
			Let's say the Lalor Lightning lose a game like this:
		</p>

		<table className="help-scores-table">
			<thead>
				<tr>
					<th />
					<th>Lightning</th>
					<th>Opponent</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Q1</td>
					<td>4</td>
					<td>6</td>
				</tr>
				<tr>
					<td>Q2</td>
					<td>8</td>
					<td>10</td>
				</tr>
				<tr>
					<td>Q3</td>
					<td>12</td>
					<td>19</td>
				</tr>
				<tr>
					<td>Final</td>
					<td>16</td>
					<td>24</td>
				</tr>
			</tbody>
		</table>

		<p>
			If we calculate the goal difference for each quarter, we can see that this game
			was mostly lost in the third quarter:
		</p>

		<table className="help-scores-table">
			<thead>
				<tr>
					<th />
					<th>Lightning</th>
					<th>Opponent</th>
					<th>Score in this qtr</th>
					<th>Goal difference</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Q1</td>
					<td>4</td>
					<td>6</td>
					<td>4 - 6</td>
					<td>-2</td>
				</tr>
				<tr>
					<td>Q2</td>
					<td>8</td>
					<td>10</td>
					<td>4 - 4</td>
					<td>0</td>
				</tr>
				<tr>
					<td>Q3</td>
					<td>12</td>
					<td>19</td>
					<td>4 - 9</td>
					<td>-5</td>
				</tr>
				<tr>
					<td>Final</td>
					<td>16</td>
					<td>24</td>
					<td>4 - 5</td>
					<td>-1</td>
				</tr>
			</tbody>
		</table>

		<p>
			AutoRoster now looks for positional correlations.
			If, for example, Annabelle, played WA / C / off / C, AutoRoster will
			note that her two quarters of C were the team's best, while she was off-court
			for their worst.
		</p>

		<p>
			This might be a coincidence — maybe the opposition played their best roster
			in that quarter, and the scoreline didn't have much to do with Annabelle.
			We shouldn't jump to conclusions from a single quarter. But after a few games,
			external factors average out, and reliable trends emerge.
		</p>

		<p>
			Stats often work best as a prompt, helping coaches identify areas of unusually high
			or low performance, and encouraging them to think about why this might be the case.
		</p>

	</>
)

const HelpPositionsPlayed = () => (
	<>
		<h2>
			Positions Played
		</h2>

		<p>
			A simple tally of the number of periods each player has played in each position.
		</p>

		<p>
			Tap the right-most column header to cycle between:
		</p>

		<ul>
			<li>
				<p>
					<b>T</b>: Total periods played
				</p>
			</li>
			<li>
				<p>
					<b>-</b>: Total periods benched
				</p>
			</li>
			<li>
				<p>
					<b>%</b>: On-court time (percentage of time not on bench)
				</p>
			</li>
		</ul>

		<p>
			You can toggle whether to <b>include future rosters</b>, and whether to
			show <b>Fill-ins</b> <FillinIcon />.
		</p>
	</>
)

const HelpNetStats = () => (
	<>
		<h2>
			NetStats
		</h2>

		<p>
			Once you've entered period-by-period scores, players can be rated for each position they've
			played. A score of zero means your team performs at its average level with the player in that position.
			A positive score means the team performs better than average.
		</p>

		<p>
			Higher scores are better. A GD with +0.50 means your team overperforms
			by half a goal per period with this player in that position.
		</p>

		<p>
			Scores are adjusted for fixture difficulty, so they're not skewed by whether your team
			wins or loses more often.
		</p>

		<p>
			To hide NetStats on rosters, tap <NavItem icon={optionsOutline} label="Settings"/> in
			the top right of any roster page in Edit Mode, then toggle <b>Show NetStats</b>.
		</p>

		<p>
			NetStats are only visible to team coaches and club admins, even if you share a link to your roster.
		</p>

	</>
)

const HelpStatsFAQ = () => (
	<>
		<h2>
			Stats FAQ
		</h2>

		<p>
			Whether your team is full of superstars or newbies, it will function more
			effectively with players in some roles rather than others. That's what
			NetStats are for &mdash; they don't really rate individual players, but they do
			tell you whether your team overperforms or underperforms with them in
			particular positions.
		</p>
		<h4>
			Q. Why doesn't my star player have good scores in every position?
		</h4>

		<p>
			Because she's still better for your team in some positions than others. Her NetStat score shows
			whether her team performs better or worse than usual when she's in that position — so she
			can be a great GA but still have a negative NetStat score because the team is more effective
			when she's in GD.
		</p>

		<p>
			This often has a lot to do with her teammates. A versatile player capable of filling
			any role will tend to score best wherever the team needs her most. For example, if
			a team is in desperate need of a GK and no-one else can play there, an all-rounder
			can have a high NetStat score in GK even though that's not her natural position.
		</p>

		<h4>
			Q. Why does my star GS have a NetStat score of zero?
		</h4>

		<p>
			Probably because she never plays anywhere else. A score of zero means the team plays at
			its average level with her in this position — which, if she's always there, must be true
			by definition.
		</p>

		<h4>
			Q. What does a negative NetStat score mean?
		</h4>

		<p>
			That the team tends to underperform when she's in that position. This is usually because
			the team tends to work better when she's somewhere else instead. See below for more.
		</p>

		<h4>
			Q. This negative NetStat score must be wrong, surely?
		</h4>
		<p>
			Reasons a good player might display a negative number:
		</p>

		<ul>
			<li>
				<p>
					As good as she is, the team works better when she's somewhere else.
				</p>
				<p>
					Say you have two players who are both excellent GDs, but only one of them can also
					play GK. The more versatile player is likely to show negative GD scores (and positive
					GK scores) because the team works better when the
					more limited player is able to stay in her preferred role.
				</p>
			</li>
			<li>
				<p>
					The season is young, with only a couple of games of data, so the numbers
					haven't settled yet. They will average out and become more reliable over time.
				</p>
			</li>
			<li>
				<p>
					A player may always get put in a role in conjunction with another move.
					For example, if Alice only plays C to give your superstar a rest,
					and those tend to be your team's worst periods, then Alice will have a negative
					NetStat score in C. She may still be your team's second-best C,
					and anyone else would score even more negatively.
				</p>
			</li>
			<li>
				<p>
					Sometimes skilled players are agents of chaos, and disrupt their teammates.
				</p>
			</li>
			<li>
				<p>
					Similarly, strong attacking players may neglect subtle but important defensive work.
				</p>
			</li>
		</ul>

		<p>
			For most teams, every player will have a positive score in one or two positions,
			which means the team plays better with her there rather than somewhere else.
		</p>

		<h4>
			Q. What is the gray score on the right underneath "-"?
		</h4>

		<p>
			That's the player's NetScore when she's on the bench. Valuable players usually have
            negative bench scores, because the team underperforms when they're off-court.
		</p>
		<p>
            Large positive bench scores mean the team overperforms with this player on
            the bench. That's usually because the player is weaker, or the team doesn't have
            a great fit for them &mdash; e.g. a solid specialist GK but your team already has a
            better one.
		</p>

		<h4>
			Q. What's the difference between Average and Season NetStats?
		</h4>

		<p>
			"Season" is just "Average" multiplied by the number of periods the player
			has spent in that position. (With some adjustment for statistical reasons.)
		</p>

		<p>
			Watch out for "Season" numbers that become strongly negative &mdash; this
			happens when you repeatedly roster players in sub-optimal positions.
		</p>
	</>
)


const HelpBestQtrs = () => (
	<>
		<h2>
			Best Quarters
		</h2>

		<p>
			A simple list of every period your team has played, ordered from best to worst in terms of:
		</p>

		<ul>
			<li>
				<p>
					<b>Overall</b>: Simple goal difference (goals scored minus goals conceded)
				</p>
			</li>
			<li>
				<p>
					<b>Attack</b>: Most goals scored, ignoring goals conceded
				</p>
			</li>
			<li>
				<p>
					<b>Defence</b>: Least goals conceded, ignoring goals scored
				</p>
			</li>
		</ul>

		<p>
			You should expect to see games against weak opponents on the left, and tougher games
			on the right.
		</p>
	</>
)

const HelpPairings = () => (
	<>
		<h2>
			Combinations (<PlanName />)
		</h2>

		<p>
			AutoRoster can determine which combinations of players work best together.
		</p>

		<p>
			For example, you may have a GA who is more effective when paired with
			a particular GS. Or a set of players who work especially well together
			as WD/GD/GK.
		</p>

		<p>
			With the free AutoRoster Basic plan, <NavItem tab="stats" /> → <b>Combinations</b> only
			your team's top Pair and Trio are available.
		</p>

		<p>
			With <PlanName />, you can view all Pairs and Trios, and these numbers are
			also displayed directly on rosters in role-based view by tapping <NavItemToggleView />.
			(Not available in A/C/D formats.)
		</p>

		<p>
			<img
				className=""
				src={comboImg}
				alt="Combinations"
			/>
		</p>

		<p>
			<em>
				In the above example roster, Neve is a mobile, athletic player who can perform
				both in the circle and in midcourt — her GA NetScore is a relatively modest
				+0.2 because while she's good for the team here, she'd also be good elsewhere.
				But while she's in GA, she's a great pairing with GS Holly, hence the +0.5.
				Neve also pairs effectively with WA Clover (+0.4), and forms
				a strong trio with WA Clover and C Emily (+1.4).
			</em>
		</p>

		<p>
			A Combination is a pair or trio of players who:
		</p>

		<ul>
			<li>
				<p>
					have played the same roles together for at least 2 periods (not necessarily in the same game)
				</p>
			</li>
			<li>
				<p>
					have adjacent roles, e.g. GS + GA or WD + GD
				</p>
			</li>
		</ul>

		<p>
			In <NavItem tab="stats" />, Combinations are ranked left to right according to
			their cumulative Season score.
			Your best combinations will be on the left, and tend to be both effective and frequently
			used.
		</p>

		<p>
			Below each combination is a list of periods played together, ordered from best to worst.
			(This is determined by their NetStat score, which takes into account the strength of the opposition, so some
			periods may appear in surprising positions. For example, a 3-3 period will be rated well if
			the team was heavily outscored in the other periods, but rated poorly if it occurred in
			a game the team won easily.)
		</p>

		<p>
			Combinations on the far right may benefit from reconsideration, as they tend to be both
			negatively correlated with performance and frequently used.
		</p>

		<p>
			AutoRoster usually uses Combinations stats internally when generating and scoring rosters,
			both for Basic and <PlanName /> users.
		</p>
	</>
)

const HelpAbsentees = () => (
	<>
		<h2>
			Attendance, Captains & Fill-Ins
		</h2>

		<p>
			You can view summary lists of attendance (both for games and training), and who's been captain or a fill-in.
		</p>

		<h5>
			Absent <FaCalendarTimes />
		</h5>
		<p>
			This shows missed games by each player. A player is considered to have missed a game
			when you mark them as unavailable in a roster by tapping
			the <NavItem icon={ellipse} iconClassName="tick" label="green dot" /> or <NavItem IconElement={TiTimes} iconClassName="cross" label="gray cross" /> beside their name.
		</p>

		<h5>
			Captain <NavItem IconElement={OrangeIcon} iconClassName="orange-image" />
		</h5>
		<p>
			Appoint a captain (or fruit-bringer) by using
			the
			<NavItem IconElement={OrangeIcon} iconClassName="orange-image" /> drop-down
			box at the bottom of each roster.
		</p>

		<h5>
			Fill-Ins <FillinIcon />
		</h5>
		<p>
			Fill-ins are players you can use in game rosters, but who don't appear in your
			team stats, and who are marked unavailable by default. This is useful when you
			want to use a player for only a few games.
		</p>
		<p>
			You can designate a player as a Fill-in when you add or edit them in <NavItem tab="players" />.
		</p>

		<h5>
			Training <IonIcon icon={training} className="help-training-icon" />
		</h5>
		<p>
			You can track player training attendance by tapping
		   	the <NavItem tab="training" /> tab, selecting a date, and tapping players.
		</p>
	</>
)


const HelpTips = (props: HelpSubAccordionProps) => {

	const { subTab, setSubTab } = props

    return (
        <>
            <p>
                Explore more of what's possible for power users:
            </p>
            <IonAccordionGroup
                value={subTab}
                onIonChange={(e: CustomEvent) => {
                    e.stopPropagation()
                    const newSubValue = e?.detail?.value
                    setSubTab(newSubValue)
                }}
            >

                <IonAccordion value="tips-sync" className="help-tips-sync">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            Fixture Sync
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsSync />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-pin" className="help-tips-pin">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <img src={pinImg} alt="" className="subimg" />
                            Pin
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsPin />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-raincloud" className="help-tips-raincloud">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <img src={raincloudImg} alt="" className="subimg" />
                            Raincloud
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsRaincloud />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-subs" className="help-tips-subs">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <img src={subImg} alt="" className="subimg" />
                            Substitutions
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsSubs />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-fillins" className="help-tips-fillins">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <FillinIcon /> Fill-Ins
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsFillins />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-prefs" className="help-tips-prefs">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <FaStreetView /> Position Preferences
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsPrefs />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-rules" className="help-tips-rules">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            <MaleIcon />
                            1 Male per Third Rule
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsRules />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-duration" className="help-tips-duration">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            Game Duration (2 Halves, 3 Periods...)
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsDuration />
                    </div>
                </IonAccordion>

                <IonAccordion value="tips-format" className="help-tips-format">
                    <IonItem slot="header" color="light">
                        <IonLabel>
                            Format (5- and 6-a-side)
                        </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                        <HelpTipsFormat />
                    </div>
                </IonAccordion>

            </IonAccordionGroup>
        </>
    )
}

const HelpTipsPin = () => (
	<>
		<p>
			Pinned positions don't move
			when you use <SampleGoButton /> to automatically generate a roster.
            To pin a position in place, tap it (without dragging). You can tap
            again to toggle off the pin.
		</p>
	</>
)

const HelpTipsRaincloud = () => (
	<>
		<p>
			The Raincloud lets you signal when people didn't actually play in their rostered positions, so
            it won't count toward their stats. For example, your last quarter might have been rained out,
            or a player was injured mid-game and you had no-one to replace them.
		</p>
		<p>
			For example, if your game was rained out at half time, you can put rainclouds on all the 3rd and
			4th quarter roles, so only the first two quarters count toward stats.
		</p>
        <p>
            Toggle Raincloud on/off by tapping a position while editing a roster. This is
            the same method as Pinning (see above), but when a game is scheduled in the past, you cycle through
            Raincloud as an extra option.
		</p>
	</>
)

const HelpTipsSubs = () => (
	<>
		<p>
			You can assign players two different positions per period.
		</p>
        <div className="video-container">
            <video playsInline autoPlay muted loop>
                <source src={subDemoClip} type="video/mp4" />
            </video>
        </div>
        <p>
            Often subs swap to/from the bench, but any combination is possible:
        </p>
        <div className="video-container">
            <video playsInline autoPlay muted loop>
                <source src={subDemoClip2} type="video/mp4" />
            </video>
        </div>
        <p>
            To create a sub:
        </p>
        <ol>
            <li>
                <p>
                    In <NavItem label="Edit" /> mode, press and hold a position until it lifts up ("long press").
                </p>
            </li>
            <li>
                <p>
                    Drag the floating section to a new location and release it.
                </p>
            </li>
        </ol>
		<p>
			To undo, drag the second half back to its original position.
		</p>
        <p>
            To control how eager the <SampleGoButton /> button is to use subs, use
            the	<NavItem icon={optionsOutline} label="" /> roster settings slider "Use rolling subs."
        </p>
		<p>
            Subs and split positions are automatically accounted for in stats. It's assumed
            the player spends half their time in each position.
		</p>
        <p>
            AutoRoster doesn't support more than two positions in a single period.
        </p>
	</>
)

const HelpTipsFillins = () => (
	<>
        <p>
            Fill-ins can be used in rosters but don't appear in team lists such as Absentees, Training, and most Stats.
            Use them for temporary players you don't want to clutter up such screens.
            Fill-ins are marked with a bird icon because they flit in and out of your team.
        </p>
        <p>
            To mark a player as a fill-in:
        </p>
        <ol>
            <li>
                Tap <NavItem tab="players" /> {'>'} <HiDotsHorizontal /> {'>'} <NavItem label="Edit Player" />
            </li>
            <li>
				Mark the checkbox beside: "This player is a fill-in <FillinIcon />"
			</li>
			<li>
				Tap <NavItem label="Update" />
			</li>
		</ol>
        <p>
            Or you can tick "This player is a fill-in" when creating them.
        </p>
	</>
)

const HelpTipsPrefs = () => (
	<>
        <p>
            You can tell AutoRoster to prefer (or require) players in particular positions:
        </p>
        <img src={posPrefsImg} alt="" />
        <p>
            Modes (toggle beneath the preferences grid):
        </p>
        <ul>
            <li>
                <p>
                    <b>Prefer</b>: "It's better when players are in any of their nominated positions." (Default.)
                </p>
            </li>
            <li>
                <p>
                    <b>Require all</b>: "Players must be given <em>all</em> their nominated positions."
                </p>
            </li>
        </ul>
        <p>
            Repeatedly tap a position to ban it.
        </p>
        <p>
            Tips:
        </p>
        <ul>
            <li>
                <p>
                    Don't overdo it! Overloading AutoRoster with preferences makes it harder to meet your
                    other priorities, such as high team performance and even court time.
                </p>
            </li>
            <li>
                <p>
                    Remove old ones: Your idea of ideal team roles will probably evolve over the season, so
                    don't handicap AutoRoster with an outdated idea of who should play where.
                    The app has an in-built preference for putting players in the same roles they've already
                    played, so after a few games it doesn't need that reinforced.
                </p>
            </li>
            <li>
                <p>
                    Consider not using position preferences at all! AutoRoster will still generate a sensible
                    mix of positions even for the season's first game &mdash; some midcourt players, some attack,
                    some defence. Drag player names to swap entire rows of positions at once.
                </p>
            </li>
        </ul>
	</>
)

const HelpTipsDuration = () => (
	<>
		<p>
			You can create a tournament of two-halves games, or some other number of periods,
			in your Season Settings. These are most easily found by navigating to the bottom of
			your <NavItem tab="games" /> tab.
		</p>
		<ul>
			<li>
				<p>
					All games within the same season must have the same number of periods.
				</p>
			</li>
			<li>
				<p>
				   	You can't change the number of periods after creating a season unless you
					first erase all its existing rosters.
				</p>
			</li>
			<li>
				<p>
					For flash games with a single period, NetStats and Combinations can't be generated,
					because AutoRoster relies on comparing periods within the same game in order to
					account for fixture bias (i.e. some opponents being stronger than others).
				</p>
			</li>
		</ul>
	</>
)

const HelpTipsFormat = () => (
	<>
		<p>
			You can use 5- and 6-a-side game formats, including
            FAST5 (no Wings), 6-a-side without WD, and the A/C/D variants used by Indoor Netball and
            New Zealand's <em>futureFERNS</em>.
		</p>
		<p>
			<img src={fivePlayersImg} alt="" />
		</p>
		<p>
            Select <b>Format</b> when creating a new season. You can also change it via Season Settings
            at the bottom of your <NavItem tab="games" /> tab, but only if you don't have any existing rosters.
            You can't mix different game formats in the same season.
		</p>
		<p>
			For A/C/D formats, Combination stats don't contribute to roster strength ratings (since
            the positions are more general), but they can still be viewed in the <NavItem tab="stats" /> tab.
		</p>
	</>
)

const HelpTipsSync = () => (
	<>
        <h4>
            Fixture Sync (Beta)
        </h4>
        <p className="fixture-sync-providers-box">
            <img className="fixture-sync-provider-image" src={netballConnectImage} alt="NetballConnect" />
            <img className="fixture-sync-provider-image" src={playHqImage} alt="PlayHQ / NetballHQ" />
        </p>

		<p>
			If you're in a league supported by NetballConnect or PlayHQ / Netball HQ, AutoRoster can fetch
			your games and official scores.
		</p>
		<p>
			You can set up Fixture Sync at the time you create a new season, or add it later via
			Season Settings at the bottom of your <NavItem tab="games" /> tab.
		</p>
		<p>
			By default, Fixture Sync will offer to add, remove, or modify your Games as needed
			to match the official fixture. If you want to ignore specific games (e.g. because the
			official fixture is wrong, or you have a practice game that isn't in the official
			fixture), use "Skip..." in the top-right of that game's listing in the pop-up Fixture
			Sync window. Skipped games are listed behind
			a label like "1 skipped game..." which you can tap to re-enable sync for them.
		</p>
        <p>
            <em>PlayHQ sync is only available in the app, not via a web browser.</em>
		</p>

	</>
)

const HelpTipsRules = () => (
	<>
		<p>
			Mixed leagues may enforce a "one male per third" rule, which limits teams to a
			maximum of one male player in each of these three groups: GS+GA, WA+C+WD, and GD+GK.
		</p>
		<p>
			AutoRoster will take care of this if you enable "Rules: 1 Male per Third"
			in your Season Settings. You can do this either when you first set up your season,
			or later by navigating to the bottom of your <NavItem tab="games" /> tab.
		</p>
		<p>
			Once enabled, you can mark players as Male <MaleIcon /> in the Players
			tab &mdash; either while adding them, or later by tapping the three dots beside
			their name and then <b>Edit player</b>.
		</p>
	</>
)

const HelpSharing = () => {

	return (
		<>
			<p>
				Sharing allows people to view your rosters. They can't make changes or see performance
				stats such as NetStats and Combinations. They can see <em>Positions Played</em> and
				<em>Attendance, Captains &amp; Fill-ins</em>.
			</p>

			<h4>
				Sharing One Roster
			</h4>
			<p>
				Tap the
				{' '}
				<IonIcon
					icon={shareOutline}
				/>
				{' '}
				Share icon in the top-right while viewing any particular roster.
				Then choose <b>Share</b> to create a link that anyone can use.
			</p>
			<p>
				If you're using a web browser, this may simply
				generate a link that you need to paste into an email or message and send.
				On a phone or tablet, it will use your device's native sharing system.
			</p>
			<p>
				If the Share icon is grayed out, your season has been marked "Private."
				Toggle this back to "Shareable" in your Season Settings.
			</p>

			<h4>
				Sharing a Whole Season
			</h4>
			<p>
				You can send a direct link to all your team's games by going to
				the <NavItem tab="games" /> and using the
				{' '}
				<IonIcon
					icon={shareOutline}
				/>
				{' '}
				Share link at the very bottom.
			</p>
			<p>
				People can also view your season's games list if you've shared any individual
				roster with them &mdash; they just need to tap <NavItem tab="games" />.
			</p>

			<h4>
				Sharing Management & Stats
			</h4>
			<p>
				If you want to let multiple people modify rosters or see stats,
                you need to create a Club (even if you only have one team).
				It's a bit more complicated, but you can do it for free!
			</p>
			<p>
				<MoreInfoLink helpSection={"clubs"} text="More about Clubs..." />
			</p>
            <ol>
                <li>
                    <p>
                        Create a Club
                    </p>
                </li>
                <li>
                    <p>
                        Invite your buddy to join your Club
                    </p>
                </li>
                <li>
                    <p>
                        Assign them as co-coach of your team
                    </p>
                </li>
            </ol>
            <p>
                Note: Co-coaches have full permission to edit and even delete teams to which they are assigned.
            </p>

			<h4>
				Private Seasons
			</h4>
			<p>
				You can prevent other people from viewing your rosters, players, and stats,
				even if you previously shared them. Control this in your Season Settings
				at the bottom of your <NavItem tab="games" /> tab.
			</p>
		</>
	)
}

const HelpTroubleshooting = () => (
	<>
		<h4>
			I can see my rosters but not edit them!
		</h4>
		<p>
			If you can view your roster but instead of a big green "Edit" button, you see
            "<b>You can't edit this roster</b>", your account isn't a coach of that team.
		</p>
		<p>
            This can happen if:
        </p>
        <ul>
            <li>
                <p>
                    You're signed out.
                </p>
            </li>
            <li>
                <p>
                    You created the team with a different account than the one you're currently using.
                    AutoRoster offers different ways to sign in (Google Account, Apple account,
                    SMS message to phone, email + password...), and if you use these to independently
                    create multiple accounts, AutoRoster can't tell they belong to the same person.
                    In this case, sign out and back in using the same method as when you created the team.
                </p>
            </li>
            <li>
                <p>
                    You belong to a club, but need a club admin to assign you as coach of this team.
                    Joining a club doesn't automatically give you control over all its teams.
                </p>
            </li>
        </ul>
		<p>
			Still stuck? Use the Email Max button at the bottom of this page!
		</p>
		<h4>
			My team has disappeared!
		</h4>
		<p>
			This can happen if you have network problems. You can usually fix by waiting, or by
			restarting the app (e.g.
			{' '}
			<a href="https://support.apple.com/en-gb/guide/iphone/iph83bfec492/ios">Apple</a>
			{' '}
			or
			{' '}
			<a href="https://www.howtogeek.com/903829/how-to-restart-an-app-on-android/">Android</a>
			), which forces it to reload everything.
		</p>
        <p>
            Rest assured AutoRoster makes it difficult to accidentally delete anything. It
            will warn you before any potentially destructive action.
        </p>
	</>
)

type EmailMaxProps = {
	subject?: string
	text?: string
}

export const EmailMax = (props: EmailMaxProps) => {

	const user = useUser()
	const section = useSection()
	const club = useClub()

	const platform = Capacitor.getPlatform()
	const versionInfo = `${packageInfo?.version}-${platform}`

	const debugInfo = `

AutoRoster debug info
~~~~~~~~~~~~~~~~~~~~~
Version   : ${versionInfo}
Platform  : ${platform}
User ID   : ${user?.uid || '-'}
Team ID   : ${section?.team || '-'}
Season ID : ${section?.season || '-'}
Game ID   : ${section?.game || '-'}
Club ID   : ${club?.id || '-'}
`

	const body = (props.text || '') + debugInfo

	const href = "mailto:max_barry@maxbarry.com?subject=" + encodeURI(props.subject || "AutoRoster") + "&body=" + encodeURI(body)

	return (
		<IonButton
			href={href}
			fill="outline"
		>
			<IonIcon icon={mail} slot="start" />
			<IonLabel>
				Email Max
			</IonLabel>
		</IonButton>
	)
}

const About = () => (
	<>
		<h2>
			About
		</h2>
		<p>
			Hi, I'm Max. I have two daughters playing netball and
			wound up coaching a kids' team. The hardest part of every week
			was figuring out the freaking roster. Then at the last minute
			one kid would be away and I could put Amy in GA but then she'd
			have no rest while other kids — arrrrgh. This is why we
			have computers, right? Right. So I built this app.
		</p>
		<p>
			Please enjoy and let me know how you go.
		</p>
		<p>
			— <a href="https://maxbarry.com" className="linkish">Max Barry</a>
		</p>

	</>
)

const HelpPrivacy = () => (
	<>
		<p>
			<em>
				Short version: This is an app I wrote myself and I didn't make it do anything bad. No ads, no
				reselling of user data. It includes services from Google (hosting, database, analytics),
                Facebook (login), and Apple (login), who collect the usual amount of stuff from all users.
                For Apple users who purchase a subscription then request a refund, we share usage data as
                part of Apple's regular refund request process.
            </em>
		</p>
		<p>
			This following document sets forth the Privacy Policy for the App. The App is bound by the Privacy Act 1988 (Cth), which sets out a number of principles concerning the privacy of individuals.
		</p>

		<h3>
			Definitions
		</h3>

		<p>
			<b>Services</b> means roster generation and similar functionality designed to aid in the management of netball games.
			<br />
			<b>The App</b> means AutoRoster, which is both an app and a website.
			<br />
			<b>We / Us</b> etc means NationStates Pty Ltd and any subsidiaries, affiliates, employees, officers, agents or assigns.
		</p>

		<h3>
			Collection of your personal information
		</h3>
		<p>
			There are many aspects of the App which can be viewed without providing personal information, however, for access to future AutoRoster customer support features you are required to submit personally identifiable information. This may include but not limited to a unique username and password, or provide sensitive information in the recovery of your lost password.
		</p>

		<h3>
			Use of your personal information
		</h3>
		<p>
			For each visitor to reach the site, we expressively collect the following non-personally identifiable information, including but not limited to browser type, version and language, operating system, pages viewed while browsing the Site, page access times and referring website address. This collected information is used solely internally for the purpose of gauging visitor traffic, trends and delivering personalized content to you while you are at this Site.
		</p>
		<p>
			From time to time, we may use customer information for new, unanticipated uses not previously disclosed in our privacy notice. If our information practices change at some time in the future we will use for these new purposes only, data collected from the time of the policy change forward will adhere to our updated practices.
		</p>

		<h3>
			Sharing of your personal information
		</h3>
		<p>
			In order to provide basic site functionality, your information may be shared with third parties who provide site services such as analytics and hosting, notably including Google, Facebook and Apple. We take reasonable steps to ensure that these organisations are bound by confidentiality and privacy obligations in relation to the protection of your personal information.
		</p>

		<h3>
			Changes to this Privacy Policy
		</h3>
		<p>
			We reserve the right to make amendments to this Privacy Policy at any time. If you have objections to the Privacy Policy, you should not access or use the Site.
		</p>

		<h3>
			Accessing Your Personal Information
		</h3>
		<p>
			You have a right to access your personal information, subject to exceptions allowed by law. If you would like to do so, please let us know. You may be required to put your request in writing for security reasons. We reserve the right to charge a fee for searching for, and providing access to, your information on a per request basis.
		</p>

		<h3>
			Data retention and deletion
		</h3>
		<p>
			AutoRoster retains active account and team data for as long as it is needed to provide the App, unless it is deleted earlier by you, by another authorised coach or manager, or by us in response to a valid deletion request.
		</p>
		<p>
			Team data, including teams, seasons, players, games, rosters, training attendance, team notes, recorded flow map data, and related statistics, is retained until it is deleted by an authorised coach or manager. When this data is deleted in the App, it is removed from the active database.
		</p>
		<p>
			If you delete your AutoRoster account, you must first leave any clubs and delete teams you solely manage, or stop coaching shared teams. Account deletion removes your login account, but does not delete shared team data that other coaches or managers continue to control.
		</p>
		<p>
			If you contact us to request deletion of personal information or team data that you are authorised to control, we will remove it from active systems within 30 days. We will not delete shared team data at the request of one coach if other authorised coaches or managers continue to use or control that team.
		</p>
		<p>
			Deleted data may remain temporarily in backups, logs, and analytics systems until those systems expire it. Firestore export backups are retained for up to 400 days, and deleted backup objects may remain recoverable through Google Cloud Storage soft delete for up to 7 additional days. App error logs are retained for 30 days, and Google Cloud audit logs are retained for 400 days.
		</p>
		<p>
			AutoRoster uses Google Analytics / Firebase Analytics. Event-level analytics data is retained for 2 months. User-level analytics data is retained for 14 months, with the retention period reset when a user is active again.
		</p>
		<p>
			For subscriptions and purchases, payment providers such as Google Play, Apple, Stripe, and RevenueCat may retain purchase, receipt, billing, and refund information under their own policies. AutoRoster does not store payment card details, but may store limited subscription identifiers needed to connect paid features to an AutoRoster user, club, or team.
		</p>

		<h3>
			CCPA Privacy Rights (Do Not Sell My Personal Information)
		</h3>
		<p>
			Under the CCPA, among other rights, California consumers have the right to:
		</p>
		<ul>
			<li>
				<p>
					Request that a business that collects a consumer's personal data disclose the categories and specific pieces of personal data that a business has collected about consumers.
				</p>
			</li>
			<li>
				<p>
					Request that a business delete any personal data about the consumer that a business has collected.
				</p>
			</li>
			<li>
				<p>

					Request that a business that sells a consumer's personal data, not sell the consumer's personal data.
				</p>
			</li>
		</ul>
		<p>
			If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us.
		</p>

		<h3>
			GDPR Data Protection Rights
		</h3>
		<p>
			We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:
		</p>
		<ul>
			<li>
				<p>
					The right to access – You have the right to request copies of your personal data. We may charge you a small fee for this service.
				</p>
			</li>
			<li>
				<p>
					The right to rectification – You have the right to request that we correct any information you believe is inaccurate. You also have the right to request that we complete the information you believe is incomplete.
				</p>
			</li>
			<li>
				<p>
					The right to erasure – You have the right to request that we erase your personal data, under certain conditions.
				</p>
			</li>
			<li>
				<p>
					The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.
				</p>
			</li>
			<li>
				<p>
					The right to object to processing – You have the right to object to our processing of your personal data, under certain conditions.
				</p>
			</li>
			<li>
				<p>
					The right to data portability – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.
				</p>
			</li>
		</ul>
		<p>
			If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us.
		</p>

		<h3>
			Children's Information
		</h3>

		<p>
			Another part of our priority is adding protection for children while using the internet. We encourage parents and guardians to observe, participate in, and/or monitor and guide their online activity.
		</p>
		<p>
			AutoRoster does not knowingly collect any Personal Identifiable Information from children under the age of 13. If you think that your child provided this kind of information on our app, we strongly encourage you to contact us immediately and we will do our best efforts to promptly remove such information from our records.
		</p>

        <h3>
            Consent to Share Consumption Data with Apple
        </h3>
        <p>
            By using the app and making in-app purchases on an Apple device, you consent to sharing of data regarding your usage and consumption of purchased content with Apple as part of our efforts to resolve refund requests. This information may include details about how you have accessed and interacted with the purchased content. The purpose of sharing this data is to help Apple make an informed decision regarding refund requests. We ensure that such data sharing is done in compliance with Apple's policies and only as necessary to process your requests.
        </p>

		<h3>
			Contacting us
		</h3>
		<p>
			If you have any questions about this Privacy Policy and would like further information, please use the following email address: <a href="mailto:max_barry@maxbarry.com">max_barry@maxbarry.com</a>.
		</p>
	</>
)

const HelpTerms = () => {

	return (
		<div className="help-terms">
			<h3>
				LICENSED APPLICATION END USER LICENSE AGREEMENT
			</h3>
			<p>
				Apps made available through the App Store are licensed, not sold, to you. Your license to each App is subject to your prior acceptance of either this Licensed Application End User License Agreement (“Standard EULA”), or a custom end user license agreement between you and the Application Provider (“Custom EULA”), if one is provided. Your license to any Apple App under this Standard EULA or Custom EULA is granted by Apple, and your license to any Third Party App under this Standard EULA or Custom EULA is granted by the Application Provider of that Third Party App. Any App that is subject to this Standard EULA is referred to herein as the “Licensed Application.” The Application Provider or Apple as applicable (“Licensor”) reserves all rights in and to the Licensed Application not expressly granted to you under this Standard EULA.
			</p>
			<p>
				a. Scope of License: Licensor grants to you a nontransferable license to use the Licensed Application on any Apple-branded products that you own or control and as permitted by the Usage Rules. The terms of this Standard EULA will govern any content, materials, or services accessible from or purchased within the Licensed Application as well as upgrades provided by Licensor that replace or supplement the original Licensed Application, unless such upgrade is accompanied by a Custom EULA. Except as provided in the Usage Rules, you may not distribute or make the Licensed Application available over a network where it could be used by multiple devices at the same time. You may not transfer, redistribute or sublicense the Licensed Application and, if you sell your Apple Device to a third party, you must remove the Licensed Application from the Apple Device before doing so. You may not copy (except as permitted by this license and the Usage Rules), reverse-engineer, disassemble, attempt to derive the source code of, modify, or create derivative works of the Licensed Application, any updates, or any part thereof (except as and only to the extent that any foregoing restriction is prohibited by applicable law or to the extent as may be permitted by the licensing terms governing use of any open-sourced components included with the Licensed Application).
			</p>
			<p>
				b. Consent to Use of Data: You agree that Licensor may collect and use technical data and related information—including but not limited to technical information about your device, system and application software, and peripherals—that is gathered periodically to facilitate the provision of software updates, product support, and other services to you (if any) related to the Licensed Application. Licensor may use this information, as long as it is in a form that does not personally identify you, to improve its products or to provide services or technologies to you.
			</p>
			<p>
				c. Termination. This Standard EULA is effective until terminated by you or Licensor. Your rights under this Standard EULA will terminate automatically if you fail to comply with any of its terms.
			</p>
			<p>
				d. External Services. The Licensed Application may enable access to Licensor’s and/or third-party services and websites (collectively and individually, "External Services"). You agree to use the External Services at your sole risk. Licensor is not responsible for examining or evaluating the content or accuracy of any third-party External Services, and shall not be liable for any such third-party External Services. Data displayed by any Licensed Application or External Service, including but not limited to financial, medical and location information, is for general informational purposes only and is not guaranteed by Licensor or its agents. You will not use the External Services in any manner that is inconsistent with the terms of this Standard EULA or that infringes the intellectual property rights of Licensor or any third party. You agree not to use the External Services to harass, abuse, stalk, threaten or defame any person or entity, and that Licensor is not responsible for any such use. External Services may not be available in all languages or in your Home Country, and may not be appropriate or available for use in any particular location. To the extent you choose to use such External Services, you are solely responsible for compliance with any applicable laws. Licensor reserves the right to change, suspend, remove, disable or impose access restrictions or limits on any External Services at any time without notice or liability to you.
			</p>
			<p>
				e. NO WARRANTY: YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT USE OF THE LICENSED APPLICATION IS AT YOUR SOLE RISK. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE LICENSED APPLICATION AND ANY SERVICES PERFORMED OR PROVIDED BY THE LICENSED APPLICATION ARE PROVIDED "AS IS" AND “AS AVAILABLE,” WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND, AND LICENSOR HEREBY DISCLAIMS ALL WARRANTIES AND CONDITIONS WITH RESPECT TO THE LICENSED APPLICATION AND ANY SERVICES, EITHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES AND/OR CONDITIONS OF MERCHANTABILITY, OF SATISFACTORY QUALITY, OF FITNESS FOR A PARTICULAR PURPOSE, OF ACCURACY, OF QUIET ENJOYMENT, AND OF NONINFRINGEMENT OF THIRD-PARTY RIGHTS. NO ORAL OR WRITTEN INFORMATION OR ADVICE GIVEN BY LICENSOR OR ITS AUTHORIZED REPRESENTATIVE SHALL CREATE A WARRANTY. SHOULD THE LICENSED APPLICATION OR SERVICES PROVE DEFECTIVE, YOU ASSUME THE ENTIRE COST OF ALL NECESSARY SERVICING, REPAIR, OR CORRECTION. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO THE ABOVE EXCLUSION AND LIMITATIONS MAY NOT APPLY TO YOU.
			</p>
			<p>
				f. Limitation of Liability. TO THE EXTENT NOT PROHIBITED BY LAW, IN NO EVENT SHALL LICENSOR BE LIABLE FOR PERSONAL INJURY OR ANY INCIDENTAL, SPECIAL, INDIRECT, OR CONSEQUENTIAL DAMAGES WHATSOEVER, INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA, BUSINESS INTERRUPTION, OR ANY OTHER COMMERCIAL DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE LICENSED APPLICATION, HOWEVER CAUSED, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, OR OTHERWISE) AND EVEN IF LICENSOR HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OF LIABILITY FOR PERSONAL INJURY, OR OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THIS LIMITATION MAY NOT APPLY TO YOU. In no event shall Licensor’s total liability to you for all damages (other than as may be required by applicable law in cases involving personal injury) exceed the amount of fifty dollars ($50.00). The foregoing limitations will apply even if the above stated remedy fails of its essential purpose.
			</p>
			<p>
				g. You may not use or otherwise export or re-export the Licensed Application except as authorized by United States law and the laws of the jurisdiction in which the Licensed Application was obtained. In particular, but without limitation, the Licensed Application may not be exported or re-exported (a) into any U.S.-embargoed countries or (b) to anyone on the U.S. Treasury Department's Specially Designated Nationals List or the U.S. Department of Commerce Denied Persons List or Entity List. By using the Licensed Application, you represent and warrant that you are not located in any such country or on any such list. You also agree that you will not use these products for any purposes prohibited by United States law, including, without limitation, the development, design, manufacture, or production of nuclear, missile, or chemical or biological weapons.
			</p>
			<p>
				h. The Licensed Application and related documentation are "Commercial Items", as that term is defined at 48 C.F.R. §2.101, consisting of "Commercial Computer Software" and "Commercial Computer Software Documentation", as such terms are used in 48 C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable. Consistent with 48 C.F.R. §12.212 or 48 C.F.R. §227.7202-1 through 227.7202-4, as applicable, the Commercial Computer Software and Commercial Computer Software Documentation are being licensed to U.S. Government end users (a) only as Commercial Items and (b) with only those rights as are granted to all other end users pursuant to the terms and conditions herein. Unpublished-rights reserved under the copyright laws of the United States.
			</p>
			<p>

				i. Except to the extent expressly provided in the following paragraph, this Agreement and the relationship between you and Apple shall be governed by the laws of the State of California, excluding its conflicts of law provisions. You and Apple agree to submit to the personal and exclusive jurisdiction of the courts located within the county of Santa Clara, California, to resolve any dispute or claim arising from this Agreement. If (a) you are not a U.S. citizen; (b) you do not reside in the U.S.; (c) you are not accessing the Service from the U.S.; and (d) you are a citizen of one of the countries identified below, you hereby agree that any dispute or claim arising from this Agreement shall be governed by the applicable law set forth below, without regard to any conflict of law provisions, and you hereby irrevocably submit to the non-exclusive jurisdiction of the courts located in the state, province or country identified below whose law governs:
			</p>
			<p>
				If you are a citizen of any European Union country or Switzerland, Norway or Iceland, the governing law and forum shall be the laws and courts of your usual place of residence.
			</p>
			<p>
				Specifically excluded from application to this Agreement is that law known as the United Nations Convention on the International Sale of Goods.
			</p>
		</div>
	)
}

export default Help
